// src/components/InstallPWA/InstallPWABanner.js
import React, { useState, useEffect, useContext } from 'react';
import { SettingsContext } from '../../contexts/SettingsContext';
import { useTranslation } from 'react-i18next'; // Import useTranslation hook
import './InstallPWA.css';
import InstallPWAModalGuide from './InstallPWAModalGuide';

// Import react-icons
import { FiDownload, FiX } from 'react-icons/fi';

const InstallPWABanner = ({ alwaysShow = false }) => {
  const [showBanner, setShowBanner] = useState(alwaysShow);
  const [showGuide, setShowGuide] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOSDevice, setIsIOSDevice] = useState(false);
  const { theme } = useContext(SettingsContext);
  const { t } = useTranslation(); // Initialize translation

  useEffect(() => {
    // If alwaysShow is true, we check for session dismissal
    if (alwaysShow) {
      const sessionDismissed = sessionStorage.getItem('pwa_banner_session_dismissed') === 'true';
      setShowBanner(!sessionDismissed);
      return;
    }
    
    // Otherwise, apply normal display logic
    const lastPrompted = localStorage.getItem('pwa_prompt_last_shown');
    const hasPromptedRecently = lastPrompted && 
      (Date.now() - parseInt(lastPrompted, 10)) < 7 * 24 * 60 * 60 * 1000;
    
    const hasUserDismissed = localStorage.getItem('pwa_prompt_dismissed') === 'true';
    const hasUserInstalled = localStorage.getItem('pwa_installed') === 'true';
    
    // Detect iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOSDevice(isIOS);
    
    // Check if app is already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone || 
                         document.referrer.includes('android-app://');
    
    // Only show for users who:
    // 1. Haven't been prompted recently (unless alwaysShow is true)
    // 2. Haven't explicitly dismissed (unless alwaysShow is true)
    // 3. Haven't already installed
    // 4. Have engaged with the app (at least visited once)
    const visitCount = parseInt(localStorage.getItem('visit_count') || '0', 10);
    localStorage.setItem('visit_count', (visitCount + 1).toString());
    
    if (!hasPromptedRecently && !hasUserDismissed && !hasUserInstalled && 
        !isStandalone && visitCount >= 0) {
      // Show banner after a slight delay to allow app to load
      setTimeout(() => {
        setShowBanner(true);
        localStorage.setItem('pwa_prompt_last_shown', Date.now().toString());
      }, 1000);
    }
    
    // Handle beforeinstallprompt event
    const beforeInstallPromptHandler = (e) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
    };
    
    // Add event listener
    window.addEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      console.log('BitDog Wallet was installed successfully!');
      localStorage.setItem('pwa_installed', 'true');
      setShowBanner(false);
      setShowGuide(false);
    });
    
    // Cleanup
    return () => {
      window.removeEventListener('beforeinstallprompt', beforeInstallPromptHandler);
    };
  }, [alwaysShow]);

  const handleDismiss = () => {
    // Hide the banner
    setShowBanner(false);
    
    if (alwaysShow) {
      // If in always-show mode (welcome page), only dismiss for this session
      sessionStorage.setItem('pwa_banner_session_dismissed', 'true');
    } else {
      // Normal dismiss behavior for other pages - store in localStorage
      localStorage.setItem('pwa_prompt_dismissed', 'true');
      
      // Reset after 30 days
      setTimeout(() => {
        localStorage.removeItem('pwa_prompt_dismissed');
      }, 30 * 24 * 60 * 60 * 1000);
    }
  };

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User response to the install prompt: ${outcome}`);
      
      // We've used the prompt, and can't use it again, discard it
      setDeferredPrompt(null);
      
      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
        localStorage.setItem('pwa_installed', 'true');
      }
    } else {
      // For iOS or other platforms without direct install API
      // Open the guide modal instead of navigating
      setShowGuide(true);
    }
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div className={`pwa-install-banner ${theme}`}>
        <div className="pwa-install-icon">
          <img src="/assets/wallet-logo.png" alt="BitDog Wallet" />
        </div>
        <div className="pwa-install-content">
          <h3>{t('settings.appInstallation')}</h3>
          <p>{t('settings.installAppDesc')}</p>
        </div>
        <div className="pwa-install-actions">
          <button 
            className="settings-button primary" 
            onClick={handleInstallClick}
          >
            {isIOSDevice ? t('settings.viewInstallGuide') : t('settings.pwa.installButton')}
          </button>
          
          {/* Updated dismiss button with backup text for mobile */}
          <button 
            className="pwa-dismiss-btn" 
            onClick={handleDismiss}
            aria-label={t('actions.close')}
          >
            <span className="dismiss-icon"><FiX /></span>
          </button>
        </div>
      </div>
      
      {showGuide && (
        <InstallPWAModalGuide onClose={() => setShowGuide(false)} />
      )}
    </>
  );
};

export default InstallPWABanner;